import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { toolsByCompanyID, toolParametersByToolID } from "graphql/queries";
import { updateTool, updateToolParameter } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { ToolCreateForm, ToolUpdateForm } from ".";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ToolParameters from "./parameters/ToolParameters";
import ActionIcon from "components/actions/ActionIcon";
import ToolPhotos from "./ToolPhotos";
import ToolDocuments from "./documents/ToolDocuments";

const Tools = () => {
  const { me } = useContext(LayoutContext);
  const [
    canCreate,
    canUpdate,
    canDelete,
    canReadParams,
    canReadPhotos,
    canReadDocs,
  ] = usePermissions([
    "tool:create",
    "tool:update",
    "tool:delete",
    "tool:read-param",
    "tool:read-photo",
    "tool:read-doc",
  ]);
  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolDetails, setToolDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    toolId: null,
    toolName: null,
  });
  const [modalTool, setModalTool] = useState({
    visible: false,
    toolId: null,
  });
  const [modalParams, setModalParams] = useState({
    visible: false,
    toolId: null,
  });
  const [modalDocs, setModalDocs] = useState({
    visible: false,
    toolId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      tools.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        actions: (
          <>
            <ActionIcon
              name={
                toolDetails?.id === item.id
                  ? strings.tools.hide
                  : strings.tools.show
              }
              icon={
                toolDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                toolDetails?.id === item.id
                  ? setToolDetails(null)
                  : setToolDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.tools.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canReadParams && (
              <ActionIcon
                name={strings.tools.params}
                icon="Tune"
                color="primary"
                onClick={() => {
                  setModalParams({
                    visible: true,
                    toolId: item.id,
                    toolName: item.name,
                  });
                }}
              />
            )}
            {canReadDocs && (
              <ActionIcon
                name={strings.tools.docs}
                icon="Folder"
                color="primary"
                onClick={() => {
                  setModalDocs({
                    visible: true,
                    toolId: item.id,
                    toolName: item.name,
                  });
                }}
              />
            )}
            {canReadPhotos && (
              <ActionIcon
                name={strings.tools.readPhotos}
                icon="PhotoLibrary"
                color="primary"
                onClick={() => openModal("read-photo", item.id)}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.tools.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    toolId: item.id,
                    toolName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tools, toolDetails?.id]
  );

  const onNext = () => {
    fetchTools(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchTools(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchTools(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalTool({ visible: true, action, toolId: id });
  const onSuccessTool = () => {
    setModalTool({ visible: false });
    fetchTools();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByTools = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setTools((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchTools(token, isNext = true, keywords) {
    const nameOperation = toolsByCompanyID;
    const nameOperationStr = "toolsByCompanyID";
    try {
      setToolsLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const toolsData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                    {
                      location: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const toolsItems = toolsData.data[nameOperationStr].items;
      setTools(toolsItems);
      getNamesByTools(toolsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: toolsData.data[nameOperationStr].nextToken,
        };
      });
      setToolsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setToolsLoading(false);
    } finally {
      setToolsLoading(false);
    }
  }
  async function toolDelete(id) {
    try {
      await toolParametersDelete(id);

      const result = await API.graphql(
        graphqlOperation(updateTool, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateTool.id;
      setTools(tools.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        toolId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  async function toolParametersDelete(toolID) {
    try {
      const results = await API.graphql(
        graphqlOperation(toolParametersByToolID, {
          toolID,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete = results.data.toolParametersByToolID.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(updateToolParameter, {
              input: { id: res.id, deletedAt: new Date().toISOString() },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones", err);
    }
  }

  useEffect(() => {
    fetchTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "25%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "25%" },
    },
    {
      key: "location",
      label: strings.table.headers.location,
      _style: { width: "25%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "25%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.tools.addTool}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {toolsLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!toolDetails?.id} style={styles.collapseCard}>
          <CCard key={toolDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{toolDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b> {toolDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.location}:</b> {toolDetails?.location}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(toolDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.toolName,
        })}
        onCancel={() => setModalDetails({ visible: false, toolId: null })}
        onConfirm={() => toolDelete(modalDetails.toolId)}
      />
      {modalTool.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalTool.visible}
          onClose={() => setModalTool({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalTool.action === "create" && strings.modalTool.addTitle}
              {modalTool.action === "update" && strings.modalTool.updateTitle}
              {modalTool.action === "read-photo" &&
                strings.modalTool.readPhotosTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalTool.action === "create" && (
                <ToolCreateForm onSuccess={onSuccessTool} />
              )}
              {modalTool.action === "update" && (
                <ToolUpdateForm
                  toolId={modalTool.toolId}
                  onSuccess={onSuccessTool}
                />
              )}
              {modalTool.action === "read-photo" && (
                <ToolPhotos toolID={modalTool.toolId} />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      {modalParams.visible && (
        <ToolParameters
          toolID={modalParams.toolId}
          toolName={modalParams.toolName}
          visible={modalParams.visible}
          onClose={() => setModalParams({ visible: false })}
        />
      )}
      {modalDocs.visible && (
        <ToolDocuments
          toolID={modalDocs.toolId}
          toolName={modalDocs.toolName}
          visible={modalDocs.visible}
          onClose={() => setModalDocs({ visible: false })}
        />
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Herramientas",
    successDeleted: "Herramienta eliminada con éxito",
    tools: {
      addTool: "Agregar Herramienta",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      params: "Parámetros",
      readPhotos: "Ver Fotos",
      docs: "Documentos",
    },
    responses: {
      errorFetch: "Error al consultar herramienta",
    },
    table: {
      headers: {
        name: "Nombre",
        location: "Ubicación",
        companyName: "Empresa",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Herramienta",
      description: "Descripción",
      location: "Ubicación",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la herramienta {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalTool: {
      addTitle: "Agregar Herramienta",
      updateTitle: "Actualizar Herramienta",
      readPhotosTitle: "Fotos de la Herramienta",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Tools;
